<script lang="ts" context="module">
	/**
	 * Fetch and process buy again/saved list swimlane product items.  Currently not using cache
	 * because saved items list must always be fresh (e.g. if user saves an item on LI, they expect
	 * to see the saved items swimlane updated at the same time).
	 * @param layoutData We get this from the landing items structure
	 * @param store The store code, e.g. KC24
	 */
	export const fetchAisleInfo = (
		layoutData: ValueFromArray<StoreLanding["layout"]>, store: string
	): Observable<Cache<{ title: string; products: ProductData[] }>> => concat(
		of({ loading: true }),
		get$<GetBuyAgainItemsResponse>(
			`ca://grocery/landing-items/stores${layoutData.endpoint}&limit=${NUM_SWIMLANE_ITEMS}`, true
		).pipe(
			switchMap((baseLayout) => {
				// Sometimes platform sends us a swimlane with no products (e.g. user does not have a 
				// saved list yet) then fillLayout throws a 404.  In that event, let's not even bother
				// making the fillLayout call.
				if (baseLayout.catalogue_keys.length === 0) return of(null);

				return fillLayout([{
					...layoutData,
					// e.g. the title could be "Buy again" or "Frequently bought items"; only `baseLayout`
					// has that information accurately; `layoutData` doesn't.
					title: baseLayout.title,
					product_selection: {
						catalogue_keys: baseLayout.catalogue_keys,
					},
				}], store).pipe(
					map((responseWithProductInfo) => {
						if (!responseWithProductInfo || responseWithProductInfo.layout_items.length === 0)
							return { title: '', products: [] };

						const layoutWithProducts = responseWithProductInfo.layout_items[0];

						return {
							title: layoutWithProducts.title,
							products: putSoldOutItemsAtEnd(layoutWithProducts.products ?? []),
						};
					}),
				);
			}),
			map((payload) => (payload ? { loading: false, payload } : { loading: false })),
		),
	);

	const getGridViewUrl = (pageUrl: Page["url"]) => "/?" + query(pageUrl)
		.keep(...LOCATION_PROPS, "vertical", "store", KEY_USER_SPECIFIC_SORT)
		.set(BUY_AGAIN, '');

	export const processDataForSideNavUpdate = (
		data: ValueFromArray<StoreLanding["layout"]>,
	): SideNavMainItem => {return {
			id: data.type,
			title: data.title,
			icon: 'icon-clock-20',
			setHref: (page, item) => getGridViewUrl(page.url),
			active: (page, item) => {
				const q = query(page.url);
				return q.has(BUY_AGAIN) && !q.hasValue(BUY_AGAIN, SAVED_LIST_FILTER_ID);
			},
		};
	};
</script>

<script lang="ts">
	import type { Cache } from "$lib/serv/core/cache";
	import type { SideNavMainItem } from "$lib/serv/nav";
	import type { Page } from "@sveltejs/kit";
	import { BUY_AGAIN, KEY_USER_SPECIFIC_SORT, SAVED_LIST_FILTER_ID } from "$lib/ui/home/groc/UserSpecificAisle.svelte";

	import { GroceryLandingSection, ProductTags, type GetBuyAgainItemsResponse, type StoreLanding,
		type ProductData } from "$lib/def/landing";
	import UserSpecificSwimlane, { NUM_SWIMLANE_ITEMS, putSoldOutItemsAtEnd } from "../UserSpecificSwimlane.svelte";
	
	import { get$ } from "$lib/serv/core/auth";
	import { fillLayout } from "$lib/serv/groc-product";
	import { query } from "$lib/util/url";
	import { BehaviorSubject, Observable, Subject, catchError, concat, map, of, switchMap, type ValueFromArray } from "rxjs";
	import { LOCATION_PROPS } from "$lib/serv/locations";

	export let data: ValueFromArray<StoreLanding["layout"]>;
	export let page: Pick<Page, "url">;

	/** Store id of the store user is browsing currently */
	let store: string;
	let refresh$ = new BehaviorSubject<boolean>(false);

	$: {
		[,store,,] = data?.endpoint.split('/'); // "/[store_code]/buy-again..."
		// i.e. refresh whenever `data` changes
		refresh$.next(true);
	}

	$: gridViewUrl = getGridViewUrl(page.url);

	const cache: Observable<Cache<{ title: string; products: ProductData[] }>> = refresh$.pipe(
		switchMap(() => fetchAisleInfo(data, store)),
		catchError((fetchError) => {
			return of({
				loading: false,
				payload: { title: '', products: [] },
				// error: new HTTPError(fetchError) // If there is an error, just hide the swimlane
			});
		}),
	);

</script>

<UserSpecificSwimlane
	cache={$cache}
	title={$cache?.payload?.title || ''}
	hiddenTags={[ ProductTags.BuyAgain ]}
	{gridViewUrl}
	on:product
/>
